import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { PENDING, IDLE, SUCCESS, DANGER } from "const"
import { MtProject, MtUser } from "server/models"
import { deepCopy, StateType } from "client/utils"
import {
    StoreType,
    AccountProps,
    UserList,
    UserFilters,
    AccountUserList,

    LoadAllUsers,
    LoadAccountUsers,
    LoadAccountGroups,
    LoadAllAccountUserGroups,
    SortUsers,
    ChangeUserStatus,
    DeleteAccountUser,
    LoadAllProjects,
    DeleteProjectUser,

    setUserList,
    setAccountUserList,
    setGroupList,
    setUserGroupList,
    setAssignedAccountUserIds,
    setAssignedAccountUserSort,
    setAssignedAccountUserFilters,
    setNotAssignedAccountUserSelected,
    addModal,
} from "client/store"
import {
    Container,
    PageContent,
    PageForm,
    Loader,
    Button,
    Feedback,
    TableSearchWrapper,
    Table,
    SortRows,
    ChangeStatus,
    DeleteRow,
} from "client/components"

import UserSearch from "client/pages/search/userSearch"
import NotAssignedUsers from "../notAssigned"
import Columns from "./columns"


const AssignedUsers = ({
    accountId = 0,
}: AccountProps) => {
    const packageList = useSelector((state: StoreType) => state.package.packageList)
    const accountPackageList = useSelector((state: StoreType) => state.accountPackage.accountPackageList)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)
    const accountUserIds = accountUserList && accountUserList[accountId] ? accountUserList[accountId] : []

    const userIds = useSelector((state: StoreType) => state.accountUser.assignedUsers.userIds)
    const sortColumn = useSelector((state: StoreType) => state.accountUser.assignedUsers.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.accountUser.assignedUsers.sortOrder)
    const filters = useSelector((state: StoreType) => state.accountUser.assignedUsers.filters)
    const reloadList = useSelector((state: StoreType) => state.accountUser.reloadAccountUserList)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        const promiseArray = [
            LoadAllUsers().then((res) => {
                dispatch(setUserList(res))
            }),
            LoadAccountUsers(accountId).then((accountUsers) => {
                const newAccountUserList: AccountUserList = { [accountId]: accountUsers }
                dispatch(setAccountUserList(newAccountUserList))
            }),
            LoadAccountGroups(accountId).then((res) => {
                dispatch(setGroupList(res))
                LoadAllAccountUserGroups(accountId, res).then((userGroupList) => {
                    dispatch(setUserGroupList(userGroupList))
                })
            }),
        ]
        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sortedUserIds = SortUsers(Object.values(userList), sortColumn, sortOrder, filters)
        const sortedAssigned = sortedUserIds.filter((userId) => accountUserIds.includes(userId))
        dispatch(setAssignedAccountUserIds(sortedAssigned))
    }, [accountUserList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MtUser, order) => {
        dispatch(setAssignedAccountUserSort(column, order))
    }

    const onFilter = (newFilters: UserFilters) => {
        dispatch(setAssignedAccountUserFilters(newFilters))
    }

    const onStatusChange: ChangeStatus = (user: MtUser, callback) => {
        ChangeUserStatus(user).then((status) => {
            callback(() => {
                const userListCopy: UserList = deepCopy(userList)
                userListCopy[user.id] = {
                    ...userListCopy[user.id],
                    status,
                }
                dispatch(setUserList(userListCopy))
            })
        })
    }

    const onDelete: DeleteRow = (user: MtUser, callback) => {
        DeleteAccountUser(user.id, accountId).then((removedRows) => {
            // remove user from account projects
            LoadAllProjects(accountId).then((projectList) => {
                const promiseArray: Promise<any>[] = []
                const projects = Object.values(projectList) as MtProject[]
                projects.forEach((project) => {
                    promiseArray.push(DeleteProjectUser(user.id, project.id))
                })
                Promise.all(promiseArray).then(() => {
                    const success = removedRows === 1
                    callback(success, () => {
                        const newAccountUserList: AccountUserList = { [accountId]: accountUserIds.filter((id) => id !== user.id) }
                        dispatch(setAccountUserList(newAccountUserList))
                    })
                })
            })
        })
    }

    const columns = Columns(accountId)

    // define max users
    const packageId = accountPackageList && accountPackageList[accountId] && accountPackageList[accountId].length
        ? accountPackageList[accountId][0].packageId
        : null
    const activePackage = packageList ? packageList[packageId] : null
    const maxUsers = activePackage ? activePackage.maxUsers : null
    let feedback
    if (maxUsers !== null) {
        if (accountUserIds.length < maxUsers) feedback = lang.maxAccountUsers
        if (accountUserIds.length === maxUsers) feedback = lang.maxAccountUsersReached
        if (accountUserIds.length > maxUsers) feedback = lang.maxAccountUsersExceeded
        feedback = feedback.replace("{0}", maxUsers.toString())
    }

    const allowedToAdd = userData.accountId === 1 // only Super Admin can add users to account

    let maxUsersReached = true
    if (maxUsers !== null && accountUserIds.length < maxUsers) maxUsersReached = false
    if (accountId === 1) maxUsersReached = false

    return (
        <Container>
            <PageContent>
                {loadState === PENDING && <Loader />}
                <TableSearchWrapper>
                    <UserSearch
                        id="assignedAccountUsers"
                        rightOffset={allowedToAdd}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        allowedToAdd
                        && (
                            <Button
                                variant="success"
                                disabled={maxUsersReached}
                                onClick={() => {
                                    dispatch(setNotAssignedAccountUserSelected(null))
                                    dispatch(addModal({
                                        key: "notAssignedUsers",
                                        content: <NotAssignedUsers accountId={accountId} />,
                                    }))
                                }}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={userList}
                    dataIds={userIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="sm"
                    showPagination
                    onSort={onSort}
                    onStatusChange={onStatusChange}
                    onDelete={onDelete}
                />
                {
                    maxUsers !== null && accountId !== 1
                    && (
                        <PageForm>
                            <Feedback variant={accountUserIds.length <= maxUsers ? SUCCESS : DANGER}>
                                {feedback}
                            </Feedback>
                        </PageForm>
                    )
                }
            </PageContent>
        </Container>
    )
}
export default AssignedUsers
